import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import GS from '../grafsync/grafsync';
import Errored from './Errored';
import { globalStyles as styles } from './style/gstheme';
import TitledTextField from './TitledTextField';
import { MAX_LONGIRI_LENGTH, RTL_REGEX } from './utils/constants';
import { debounce, detailedCheckIRI, makeCancelable, sanitiseText } from './utils/utils';

// Do not allow RDF, RDFS, OWL, SHACL or SKOS
const BANNED_IRI_DOMAINS = [
    "http://www.w3.org/2004/02/skos",
    "http://www.w3.org/2008/05/skos-xl#",
    "http://www.w3.org/2002/07/owl",
    "http://www.w3.org/ns/shacl",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns",
    "http://www.w3.org/2000/01/rdf-schema#"
];

class IRIentry extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isValidIRI: false,
            isDeleted: false,
            iri: this.props.startIRI

        };
    }

    componentDidMount() {
        this.resetIRI();
    }

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    findIRI = async (value) => {
        try {
            let data = {};
            if (GS.isDeleted(value, this.props.graph)) {
                data = {
                    exists: true,
                    deleted: true
                };
            }
            else {
                // We do not include deleted IRIs
                data = await this.doFind({
                    value,
                    graph: this.props.graph,
                    includeReserved: true
                }).promise;
            }
            // If the IRI has been edited whilst we are checking ignore result
            const trimmedText = sanitiseText(this.state.iri.trim());
            if (value !== trimmedText) return;
            let isDuplicate = data && data.exists;
            if (isDuplicate && this.props.handleIRIclash) {
                value = value + "-" + Date.now();
                this.setState({ iri: value });
                isDuplicate = false;
            }
            const isDeleted = data && data.deleted;
            this.setState({
                isDuplicate,
                checked: true,
                isDeleted,
                isValidIRI: true,
                checking: false
            });
            if (!isDuplicate && this.props.confirmUniqueIRI) {
                this.props.confirmUniqueIRI(value);
            }
            else {
                if (this.props.rejectIRI) this.props.rejectIRI();
            }
        }
        catch (e) {
            if (e.message === "The requested IRI cannot be processed.") {
                this.setState({
                    isDuplicate: false,
                    checking: false,
                    checked: true,
                    isValidIRI: false
                });
                return;
            }
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                this.setState({ isErrored: e, checking: false, isValidIRI: false });
            }
        }
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    resetIRI = () => {
        this.handleIRIChange({
            target: {
                name: "iri",
                value: this.props.startIRI
            }
        });
    };

    handleIRIChange = event => {
        this.props.resetIRIconfirmation();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const text = t.value;
        const trimmedText = sanitiseText(text.trim());
        this.setState({
            [t.name]: trimmedText,
            checking: true,
            isDuplicate: false,
            isDeleted: false,
            checked: false
        }, () => {
            const isBannedIRI = BANNED_IRI_DOMAINS.some(bd => trimmedText.toLowerCase().startsWith(bd));
            const isSubsetIRI = trimmedText.length > 0 && this.props.componentIRI && this.props.componentIRI.startsWith(trimmedText);
            if (isBannedIRI || isSubsetIRI || trimmedText.length === 0 || !detailedCheckIRI(trimmedText)) {
                this.setState({ isValidIRI: false, checking: false, checked: true });
            }
            else {
                debounce(this.findIRI, this, [trimmedText]);
            }
        });
    };

    clearError = async () => this.setState({ isErrored: false });

    render() {

        const { classes, title, margin, visible, disabled, startIRI, dataTest } = this.props;
        const { iri, isErrored, isDuplicate, checked, checking, isValidIRI, isDeleted } = this.state;

        if (isErrored) return <Errored dataTest="id-error" data={isErrored} clearError={this.clearError} />;

        if (!visible) return null;

        const containsRTL = iri && iri.match(RTL_REGEX);

        let icon = null;
        let additionalIcon = null;
        if (checking) {
            icon =
                <Tooltip title="Waiting for server to check IRI">
                    <InputAdornment position="end">
                        <span>...</span>
                    </InputAdornment>
                </Tooltip>;
        }
        else if (iri.length > 0 && !isDuplicate && checked && isValidIRI) {
            icon =
                <InputAdornment size="small" position="end">
                    <Tooltip title="IRI valid" className={classes.smallIconButton}>
                        <CheckCircleOutlineIcon fontSize="small" />
                    </Tooltip>
                </InputAdornment>;
        }
        else if (isDuplicate || (!isValidIRI && iri.length > 0)) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title={
                        containsRTL ?
                            "Right-to-left text is not supported."
                            :
                            "The value is either invalid or duplicating an existing IRI"
                    }>
                        <WarningIcon data-test="warning" />
                    </Tooltip>
                </InputAdornment>;
        }
        if (!checking && iri !== startIRI) {
            additionalIcon =
                <InputAdornment position="end" onClick={this.resetIRI}>
                    <Tooltip title="Reset to current IRI">
                        <IconButton aria-label="Reset IRI" data-test="reset-iri" className={classes.smallIconButton}>
                            <RefreshIcon data-test="refresh" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }

        return (
            <div data-test={dataTest}>
                <TitledTextField
                    dataTest="iri"
                    name="iri"
                    title={title}
                    margin={margin}
                    disabled={disabled}
                    required={!disabled}
                    text={iri}
                    placeholder={
                        disabled ?
                            "Automatically computed IRI will be displayed here"
                            :
                            "Automatically computed IRI will be displayed here for you to edit"
                    }
                    maxLength={MAX_LONGIRI_LENGTH}
                    adornment={
                        <div style={{ display: 'inline-flex' }}>
                            {additionalIcon}
                            {icon}
                        </div>
                    }
                    helperText={
                        isDeleted ?
                            "This is a deleted IRI and cannot be used. Please change."
                            :
                            isDuplicate ?
                                "This IRI already exists. Please change."
                                :
                                !isValidIRI && iri.length > 0 && this.props.componentIRI && this.props.componentIRI.startsWith(iri) ?
                                    "The IRI cannot be a subset of the component IRI."
                                    :
                                    ""}
                    onChange={this.handleIRIChange} />
            </div>
        );
    }
}

IRIentry.defaultProps = {
    title: "IRI",
    margin: true,
    visible: true,
    disabled: false,
    handleIRIclash: false,
    dataTest: "iri-entry-field"
};

IRIentry.propTypes = {
    title: PropTypes.string,
    dataTest: PropTypes.string,
    margin: PropTypes.bool,
    visible: PropTypes.bool,
    disabled: PropTypes.bool,
    componentIRI: PropTypes.string,
    handleIRIclash: PropTypes.bool,
    startIRI: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    resetIRIconfirmation: PropTypes.func.isRequired,
    confirmUniqueIRI: PropTypes.func.isRequired,
    rejectIRI: PropTypes.func,
    classes: PropTypes.object
};

export default withStyles(styles)(IRIentry);